import { Box, Button, Flex, Grid, Tag, Text, useToast } from "@chakra-ui/react";
import { useState } from "react";
import { DateTime } from "luxon";
import { FaShip, FaSyncAlt } from "react-icons/fa";
import { crm } from "../../../theme";
import { ProcessViewModel } from "../../../models/Process";
import { syncProcessCargo } from "../../../services/siscomexService";
import SectionCard from "./SectionCard";

const fmt = (iso?: string, withTime = true) =>
  iso ? DateTime.fromISO(iso, { setZone: true }).toFormat(withTime ? "dd/LL/yyyy HH:mm" : "dd/LL/yyyy") : undefined;

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <Box>
    <Text fontSize="12px" color={crm.textFaint}>
      {label}
    </Text>
    <Text fontSize="14px" fontWeight="600" color={value ? crm.text : crm.textFaint}>
      {value || "—"}
    </Text>
  </Box>
);

/** Seção "Carga (governo)": CE-Mercante (marítimo) ou CCT (aéreo) + botão de sincronização manual. */
const CargoPanel = ({ process, onSynced }: { process: ProcessViewModel; onSynced: () => Promise<unknown> | void }) => {
  const toast = useToast();
  const [syncing, setSyncing] = useState(false);
  const d = process.cargoDetails;
  const isAir = d ? d.source === "CCT" : /A[ÉE]REO/i.test(process.transportMode ?? "");

  const sync = async () => {
    if (!process.id) return;
    setSyncing(true);
    try {
      const result = await syncProcessCargo(process.id);
      if (result.error) {
        const [code, ...rest] = result.error.split(": ");
        toast({
          title: code === "COOLDOWN" ? "Aguarde para consultar de novo" : "Falha ao consultar a carga",
          description: rest.join(": ") || result.error,
          status: code === "COOLDOWN" ? "warning" : "error",
        });
      } else {
        toast({
          title: result.changes.length ? "Carga atualizada" : "Carga consultada, sem alterações",
          description: result.changes.length ? result.changes.join(" · ") : undefined,
          status: "success",
        });
      }
      await onSynced();
    } catch {
      toast({ title: "Falha ao consultar a carga", status: "error" });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <SectionCard
      icon={<FaShip />}
      title="Carga (governo)"
      subtitle={
        process.cargoCheckedAt
          ? `${isAir ? "CCT · Portal Único" : "Mercante"} · consultado em ${fmt(process.cargoCheckedAt)}`
          : isAir
          ? "CCT · Portal Único · ainda não consultado"
          : "Mercante · ainda não consultado"
      }
      actions={
        <Button
          size="sm"
          variant="outline"
          borderColor={crm.border}
          color={crm.text}
          leftIcon={<FaSyncAlt />}
          isLoading={syncing}
          loadingText="Consultando"
          onClick={sync}
          title="Consultar esta carga agora no CCT / Mercante"
        >
          Sincronizar carga
        </Button>
      }
    >
      {!d ? (
        <Text px={{ base: "16px", md: "24px" }} py="20px" color={crm.textFaint} fontSize="14px">
          Sem dados da carga ainda. O robô consulta a cada 3 horas, ou use "Sincronizar carga".
        </Text>
      ) : (
        <Box px={{ base: "16px", md: "24px" }} py="20px">
          <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }} gap="16px">
            {d.source === "MERCANTE" ? (
              <>
                <Field label="CE-Mercante" value={d.ce} />
                <Field label="Manifesto" value={d.manifest} />
                <Field label="Navio / viagem" value={[d.vessel, d.voyage].filter(Boolean).join(" / ")} />
                <Field label="Porto de descarga" value={d.port} />
                <Field label="Atracação" value={fmt(d.berthingDate)} />
                <Field label="Situação do CE" value={d.situation} />
              </>
            ) : (
              <>
                <Field label="Situação da carga" value={d.situation} />
                <Field label="Voo" value={d.flight} />
                <Field label="Chegada" value={fmt(d.arrivalDate)} />
                <Field label="Recinto" value={d.warehouse} />
              </>
            )}
            <Field
              label="Presença de carga"
              value={d.cargoPresent ? `Sim${d.cargoPresenceDate ? ` · ${fmt(d.cargoPresenceDate)}` : ""}` : "Não"}
            />
          </Grid>

          <Flex mt="16px" gap="8px" wrap="wrap" align="center">
            <Text fontSize="12px" color={crm.textFaint}>
              Bloqueios
            </Text>
            {d.blocks?.length ? (
              d.blocks.map((b) => (
                <Tag key={b} colorScheme="red" borderRadius="8px">
                  {b}
                </Tag>
              ))
            ) : (
              <Tag colorScheme="green" borderRadius="8px">
                Nenhum
              </Tag>
            )}
          </Flex>

          {d.events?.length > 0 && (
            <Box mt="16px">
              <Text fontSize="12px" color={crm.textFaint} mb="4px">
                Eventos
              </Text>
              {d.events.map((e, i) => (
                <Flex key={i} gap="12px" fontSize="13px" py="2px">
                  <Text color={crm.textFaint} minW="120px">
                    {fmt(e.date) ?? "—"}
                  </Text>
                  <Text>{e.description}</Text>
                </Flex>
              ))}
            </Box>
          )}
        </Box>
      )}
    </SectionCard>
  );
};

export default CargoPanel;
